<template>
    <div
        v-if="isLoading || examHasConcepts"
        v-dark
        class="weakest-concepts"
    >
        <div v-dark class="weakest-concepts__head">
            <div v-dark class="weakest-concepts__title">
                <Icon type="concept" />
                <h2>{{ breakpoint === 'black-bear' ? 'Weak Concepts' : 'Weakest Concepts' }}</h2>
                <Tag
                    class="weakest-concepts__beta"
                    :is-beta-tag="true"
                    :is-dark-mode="isDarkMode"
                >
                    <template #tagMessage>
                        Beta
                    </template>
                </Tag>
            </div>
            <div
                v-if="!isLoading && hasPremiumAccess && weakestConcepts.length"
                v-dark
                class="weakest-concepts__column-titles"
            >
                <span>Score</span>
                <span>Start Quiz</span>
            </div>
        </div>
        <div v-if="isLoading" class="weakest-concepts__loading">
            <Icon
                type="loading2"
                :is-dark-mode="isDarkMode"
            />
        </div>
        <div v-else-if="!hasPremiumAccess" class="weakest-concepts__empty-state">
            <img
                v-if="isDarkMode"
                class="weakest-concepts__upgrade-image"
                src="@/assets/stats/weakest-concepts-upgrade-dm.png"
                alt=""
            >
            <img
                v-else
                class="weakest-concepts__upgrade-image"
                src="@/assets/stats/weakest-concepts-upgrade.png"
                alt=""
            >
            <div v-dark class="weakest-concepts__empty-state-message">
                You need a Premium subscription to see your weakest concepts.
            </div>
            <PocketButton
                :is-dark-mode="isDarkMode"
                @click="openUpgradeModal"
            >
                Upgrade to Premium
            </PocketButton>
        </div>
        <div v-else-if="!weakestConcepts.length" class="weakest-concepts__empty-state">
            <img
                v-if="isDarkMode"
                class="weakest-concepts__empty-image"
                src="@/assets/brain/brain-pencil-paper-dm.png"
                alt=""
            >
            <img
                v-else
                class="weakest-concepts__empty-image"
                src="@/assets/brain/brain-pencil-paper.png"
                alt=""
            >
            <div v-dark class="weakest-concepts__empty-state-message">
                We’re still getting to know you. Take a few quizzes and we’ll identify your weakest concepts.
            </div>
            <PocketButton
                :is-dark-mode="isDarkMode"
                :disabled="isLoadingQuick10"
                @click="clickQuick10"
            >
                Take a Quick 10
            </PocketButton>
        </div>
        <template v-else>
            <div class="weakest-concepts__list">
                <button
                    v-for="weakestConcept in weakestConcepts"
                    :key="weakestConcept.conceptQuizId"
                    v-dark
                    class="weakest-concepts__concept"
                    :aria-label="`Start quiz on ${weakestConcept.concept}, score ${formatScore(weakestConcept)}`"
                    @click="clickConcept(weakestConcept)"
                    @mousedown.prevent
                >
                    <span class="weakest-concepts__concept-name">{{ weakestConcept.concept }}</span>
                    <span v-dark class="weakest-concepts__concept-score">{{ formatScore(weakestConcept) }}</span>
                    <span v-dark class="weakest-concepts__concept-arrow">
                        <Icon type="arrow" />
                    </span>
                </button>
            </div>
            <div class="weakest-concepts__feedback">
                <div v-dark class="weakest-concepts__feedback-prompt">
                    Does this list help you focus your studying?
                </div>
                <div class="weakest-concepts__feedback-thumbs" role="group">
                    <button
                        v-for="thumb in thumbs"
                        :key="thumb.rating"
                        v-dark
                        class="weakest-concepts__feedback-thumb"
                        :class="{
                            'weakest-concepts__feedback-thumb--down': thumb.rating === 'Thumbs Down',
                            'weakest-concepts__feedback-thumb--selected': rating === thumb.rating,
                        }"
                        :aria-label="thumb.rating"
                        :aria-pressed="rating === thumb.rating"
                        @click="rate(thumb.rating)"
                        @mousedown.prevent
                        @mouseenter="hoveredThumb = thumb.rating"
                        @mouseleave="hoveredThumb = null"
                        @focus="hoveredThumb = thumb.rating"
                        @blur="hoveredThumb = null"
                    >
                        <Tooltip
                            v-if="hoveredThumb === thumb.rating && breakpoint !== 'black-bear'"
                            class="weakest-concepts__feedback-tooltip"
                            :is-dark-mode="isDarkMode"
                        >
                            {{ thumb.tooltip }}
                        </Tooltip>
                        <Icon :type="rating === thumb.rating ? 'thumbsUpOutlineFilled' : 'thumbsUpOutline'" />
                    </button>
                </div>
            </div>
            <WeakestConceptQuizModal
                v-if="showConceptQuizModal && clickedConcept"
                :weakest-concept="clickedConcept"
                @close="showConceptQuizModal = false"
                @started="trackConceptEvent('Weakest Concepts Quiz Started', clickedConcept)"
            />
            <WeakestConceptsFeedbackModal
                v-if="showFeedbackModal"
                @close="showFeedbackModal = false"
                @submit="submitFeedback"
            />
        </template>
        <UnfinishedQuizModal
            v-if="unfinishedQuizMode && clickedMode"
            :unfinished-mode="unfinishedQuizMode"
            :clicked-mode="clickedMode"
            @close="unfinishedQuizMode = null"
            @startNew="beginQuizMode"
            @resume="resumeUnfinishedQuiz"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import type { Study } from '@pocketprep/types'
import WeakestConceptQuizModal from '@/components/Stats/WeakestConceptQuizModal.vue'
import WeakestConceptsFeedbackModal from '@/components/Stats/WeakestConceptsFeedbackModal.vue'
import UnfinishedQuizModal from '@/components/Study/UnfinishedQuizModal.vue'
import type { TQuizMode } from '@/store/quiz/state'
import { analyticsModule } from '@/store/analytics/module'
import { bundleModule } from '@/store/bundle/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { quizModule } from '@/store/quiz/module'
import { screenModule } from '@/store/screen/module'
import { subscriptionModule } from '@/store/subscription/module'
import { toastModule } from '@/store/toast/module'
import { upgradeModule } from '@/store/upgrade/module'
import { userModule } from '@/store/user/module'

const {
    Icon,
    Tag,
    Tooltip,
    Button: PocketButton,
} = UIKit

type TRating = 'Thumbs Up' | 'Thumbs Down'

const thumbs: { rating: TRating; tooltip: string }[] = [
    { rating: 'Thumbs Up', tooltip: 'Helpful' },
    { rating: 'Thumbs Down', tooltip: 'Not Really' },
]

const emit = defineEmits<{
    loaded: [weakestConceptsCount: number | null]
}>()

const router = useRouter()

const isLoading = ref(true)
const isLoadingQuick10 = ref(false)
const examHasConcepts = ref(false)
const weakestConcepts = ref<Study.Cloud.TWeakestConcept[]>([])
const clickedConcept = ref<Study.Cloud.TWeakestConcept | null>(null)
const showConceptQuizModal = ref(false)
const clickedMode = ref<TQuizMode | null>(null)
const unfinishedQuizMode = ref<TQuizMode | null>(null)
const rating = ref<TRating | null>(null)
const hoveredThumb = ref<TRating | null>(null)
const showFeedbackModal = ref(false)

const isDarkMode = computed(() => userModule.state.settings.isDarkMode)

const breakpoint = computed(() => screenModule.getters.getBreakpoint())

const hasPremiumAccess = computed(() => {
    const currentExam = examMetadataModule.getters.getCurrentExamMetadata()
    return !!subscriptionModule.getters.getSubscriptionForExamId(currentExam?.objectId)
})

const weakestConceptsCount = computed(() => weakestConcepts.value.length)

onMounted(async () => {
    try {
        await examMetadataModule.actions.fetchExamMetadata()
        const examMetadataId = examMetadataModule.getters.getCurrentExamMetadata()?.objectId
        if (examMetadataId) {
            // hasPremiumAccess reads subscriptions and bundles, so wait for them too or a Premium user
            // briefly sees the upgrade state when weakest concepts resolves first
            const [ response ] = await Promise.all([
                quizModule.actions.fetchWeakestConcepts(examMetadataId),
                userModule.actions.fetchUserData(),
                bundleModule.actions.fetchBundles(),
            ])
            examHasConcepts.value = response.examHasConcepts
            weakestConcepts.value = response.weakestConcepts
        }
    } finally {
        isLoading.value = false
        emit('loaded', examHasConcepts.value ? weakestConceptsCount.value : null)
    }
})

const trackConceptEvent = (eventName: string, weakestConcept: Study.Cloud.TWeakestConcept) => {
    analyticsModule.actions.amplitudeTrack(eventName, {
        concept: weakestConcept.concept,
        conceptRank: weakestConcepts.value.indexOf(weakestConcept) + 1,
        conceptScore: Math.round(weakestConcept.averageScore * 100),
        weakestConceptsCount: weakestConceptsCount.value,
    })
}

const formatScore = (weakestConcept: Study.Cloud.TWeakestConcept) =>
    `${Math.round(weakestConcept.averageScore * 100)}%`

const openUpgradeModal = () => {
    analyticsModule.actions.amplitudeTrack('Weakest Concepts Upgrade Clicked')
    upgradeModule.actions.openUpgradeModal()
}

const clickQuick10 = async () => {
    analyticsModule.actions.amplitudeTrack('Weakest Concepts Empty State Clicked')
    await clickQuizMode('quick10')
}

const clickConcept = async (weakestConcept: Study.Cloud.TWeakestConcept) => {
    trackConceptEvent('Weakest Concepts Quiz Opened', weakestConcept)
    clickedConcept.value = weakestConcept
    await clickQuizMode('concept')
}

const rate = (clickedRating: TRating) => {
    const isClearing = rating.value === clickedRating
    rating.value = isClearing ? null : clickedRating
    analyticsModule.actions.amplitudeTrack('Weakest Concepts Rated', {
        rating: rating.value ?? 'None',
        weakestConceptsCount: weakestConceptsCount.value,
    })
    if (rating.value === 'Thumbs Down') {
        showFeedbackModal.value = true
    }
}

const submitFeedback = (feedback: string) => {
    analyticsModule.actions.amplitudeTrack('Weakest Concepts Feedback Submitted', {
        feedback,
        weakestConceptsCount: weakestConceptsCount.value,
    })
    toastModule.actions.displayToast({ title: 'Thank you for your feedback!' })
    showFeedbackModal.value = false
}

const clickQuizMode = async (mode: TQuizMode) => {
    clickedMode.value = mode
    unfinishedQuizMode.value = quizModule.getters.getUnfinishedQuizMode()
    if (!unfinishedQuizMode.value) {
        await beginQuizMode(mode)
    }
}

const beginQuizMode = async (mode: TQuizMode) => {
    unfinishedQuizMode.value = null
    if (mode === 'concept') {
        showConceptQuizModal.value = true
    } else {
        await startQuick10()
    }
}

const resumeUnfinishedQuiz = (mode: TQuizMode) => quizModule.actions.resumeUnfinishedQuiz(mode)

const startQuick10 = async () => {
    if (isLoadingQuick10.value) {
        return
    }

    isLoadingQuick10.value = true
    try {
        await quizModule.actions.createActiveQuiz({ mode: 'quick10', questionCount: 10 })

        router.push({ name: 'quiz' })
    } finally {
        isLoadingQuick10.value = false
    }
}
</script>

<style lang="scss" scoped>
.weakest-concepts {
    background: $white;
    border: 1px solid $gray-divider;
    border-radius: 6px;
    padding: 11px 14px 12px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    position: relative;

    &--dark {
        color: $fog;
        background: $brand-black;
        border-color: $slate;
    }

    &__head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        box-sizing: border-box;
        height: 32px;
        padding-bottom: 7px;
        border-bottom: 2px solid $gray-divider;
        padding-left: 8px;

        &--dark {
            padding-bottom: 8px;
            border-color: $ash;
        }
    }

    &__title {
        display: flex;
        align-items: center;

        svg {
            height: 23px;
            width: 21px;
            margin-right: 8px;
            color: $slate;
        }

        &--dark {
            color: $white;
        }

        &--dark svg {
            color: $fog;
        }

        h2 {
            margin: 0 8px 0 0;
            font-size: 16px;
            line-height: 22px;
            font-weight: 600;
            white-space: nowrap;
        }
    }

    &__column-titles {
        display: flex;
        gap: 12px;
        font-size: 12px;
        line-height: 16px;
        font-weight: 500;
        color: $brand-black;
        white-space: nowrap;

        @include breakpoint(black-bear) {
            gap: 8px;
        }

        &--dark {
            color: $fog;
        }
    }

    &__loading {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 200px;
    }

    &__empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 20px;
        padding: 24px 0 25px;
    }

    &__empty-image {
        width: 131px;
        height: 92px;
    }

    &__upgrade-image {
        width: 260px;
    }

    &__empty-state-message {
        width: 260px;
        font-size: 14px;
        line-height: 19px;
        font-weight: 500;
        text-align: center;
        color: $ash;

        &--dark {
            color: $fog;
        }
    }

    &__list {
        display: flex;
        flex-direction: column;
    }

    &__concept {
        display: flex;
        align-items: center;
        gap: 16px;
        width: 100%;
        padding: 9px 0 9px 4px;
        border: none;
        border-bottom: 1px solid $gray-divider;
        background: transparent;
        font: inherit;
        text-align: left;
        color: $brand-black;
        cursor: pointer;

        &:hover,
        &:focus-visible {
            background: $gray-background;
        }

        &--dark {
            color: $white;
            border-color: $ash;

            &:hover,
            &:focus-visible {
                background: $pickled-bluewood;
            }
        }
    }

    &__concept-name {
        flex: 1;
        min-width: 0;
        font-size: 14px;
        line-height: 19px;
        font-weight: 500;
    }

    &__concept-score {
        font-size: 14px;
        line-height: 19px;
        font-weight: 600;
        text-align: right;
        white-space: nowrap;
        color: $slate;

        &--dark {
            color: $white;
        }
    }

    &__concept-arrow {
        display: flex;
        align-items: center;
        padding: 3px 18px;
        color: $brand-blue;

        @include breakpoint(black-bear) {
            padding: 3px 16px;
        }

        svg {
            width: 16px;
            height: 12px;
        }

        &--dark {
            color: $banana-bread;
        }
    }

    &__feedback {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 8px 2px 0;
    }

    &__feedback-prompt {
        font-size: 13px;
        line-height: 18px;
        font-weight: 500;
        color: $ash;

        &--dark {
            color: $fog;
        }
    }

    &__feedback-thumbs {
        display: flex;
        gap: 6px;
    }

    &__feedback-tooltip {
        position: absolute;
        bottom: 100%;
        left: 50%;
    }

    &__feedback-thumb {
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 32px;
        height: 32px;
        padding: 0;
        border: none;
        border-radius: 6px;
        background: transparent;
        color: $slate;
        cursor: pointer;

        svg {
            width: 19px;
            height: 19px;
        }

        &:hover,
        &:focus-visible {
            background: $gray-background;
        }

        &--dark {
            color: $cloudy;

            &:hover,
            &:focus-visible {
                background: $moonlit-ocean;
                color: $banana-bread;
            }
        }

        &--down svg {
            transform: rotate(180deg);
        }
    }
}
</style>
